import React, { createContext, useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface ThemeContextType {
  isDark: boolean
  toggleTheme: () => void
  t: (key: string) => string
  i18n: any
  changeLanguage: (lng: 'en' | 'sw') => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState(true)
  const { t, i18n } = useTranslation()

  useEffect(() => {
    const saved = localStorage.getItem('theme')
    if (saved) setIsDark(saved === 'dark')
  }, [])

  useEffect(() => {
    const root = document.documentElement
    if (isDark) {
      root.classList.add('dark')
      document.body.style.backgroundColor = '#0a0a0f'
      document.body.style.color = '#ffffff'
    } else {
      root.classList.remove('dark')
      document.body.style.backgroundColor = '#f0f4f8'
      document.body.style.color = '#0f172a'
    }
  }, [isDark])

  const toggleTheme = () => {
    const next = !isDark
    setIsDark(next)
    localStorage.setItem('theme', next ? 'dark' : 'light')
  }

  const changeLanguage = (lng: 'en' | 'sw') => i18n.changeLanguage(lng)

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, t, i18n, changeLanguage }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}

export default ThemeContext